/**
 * Copiloto de IA no compositor: até 3 sugestões que a vendedora usa para
 * editar (toque no cartão) ou envia como estão (botão de enviar do cartão).
 * Nada sai sem um clique dela. O menu (ícone de brilho) pede nova sugestão,
 * reescreve o texto, oferece alternativa, tenta fechar a venda ou resume.
 */
import type { AcaoCopiloto, AvaliacaoIa, ConversaResumo, SugestoesIa } from "@ydchat/shared";
import { ACOES_REESCRITA, ROTULO_ACAO_COPILOTO, ROTULO_AVALIACAO_IA } from "@ydchat/shared";
import {
  FileText,
  Handshake,
  Heart,
  Loader2,
  PackageSearch,
  RefreshCw,
  Scissors,
  Send,
  Sparkles,
  Tag,
  ThumbsDown,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { ItemMenu, Popover } from "@/components/ui/popover";
import { cn } from "@/lib/cn";
import { useAcoesCopiloto, useCopiloto } from "@/lib/copiloto";

export interface UsoSugestaoLocal {
  interacaoId: string;
  indice: number;
  original: string;
  /** Reescrita do texto do campo: ao enviar, o campo fica vazio. */
  reescrita: boolean;
}

export function useCopilotoConversa(conversa: ConversaResumo) {
  const estado = useCopiloto(conversa.id);
  const acoes = useAcoesCopiloto(conversa.id);
  const [resumo, setResumo] = useState<string | null>(null);
  useEffect(() => setResumo(null), [conversa.id]);
  return {
    disponivel: estado.data?.disponivel ?? false,
    atual: estado.data?.atual ?? null,
    pensando: acoes.pedir.isPending,
    acaoPendente: acoes.pedir.variables?.acao ?? null,
    resumo,
    resumindo: acoes.resumir.isPending,
    fecharResumo: () => setResumo(null),
    pedir: (acao: AcaoCopiloto, base?: string | null) => acoes.pedir.mutate({ acao, base }),
    resumir: () => acoes.resumir.mutate(undefined, { onSuccess: (r) => setResumo(r.resumo) }),
    descartar: (id: string) => acoes.descartar.mutate(id),
    avaliar: (interacaoId: string, avaliacao: AvaliacaoIa) =>
      acoes.avaliar.mutate({ interacaoId, avaliacao }),
  };
}
export type Copiloto = ReturnType<typeof useCopilotoConversa>;

const ICONE: Record<AcaoCopiloto, typeof Sparkles> = {
  sugerir: Sparkles,
  mais_curta: Scissors,
  mais_simpatica: Heart,
  mais_comercial: Tag,
  alternativa: PackageSearch,
  fechar_venda: Handshake,
};

/** Menu do copiloto (fica junto dos ícones do compositor). */
export function MenuCopiloto({
  c,
  texto,
  soResumo,
}: {
  c: Copiloto;
  texto: string;
  soResumo: boolean;
}) {
  if (!c.disponivel) return null;
  const base = texto.trim() || c.atual?.sugestoes[0]?.texto || "";
  const item = (acao: AcaoCopiloto, fechar: () => void) => {
    const reescrita =
      acao === "mais_curta" || acao === "mais_simpatica" || acao === "mais_comercial";
    return (
      <ItemMenu
        key={acao}
        icone={ICONE[acao]}
        disabled={c.pensando || (reescrita && !base)}
        descricao={
          reescrita
            ? texto.trim()
              ? "Reescreve o que você escreveu"
              : base
                ? "Reescreve a 1ª sugestão"
                : "Escreva algo antes"
            : undefined
        }
        onClick={() => {
          fechar();
          c.pedir(acao, reescrita ? base : null);
        }}
      >
        {ROTULO_ACAO_COPILOTO[acao]}
      </ItemMenu>
    );
  };
  return (
    <Popover
      lado="cima"
      alinhamento="inicio"
      largura="w-64"
      gatilho={({ aberto, alternar }) => (
        <button
          onClick={alternar}
          aria-expanded={aberto}
          aria-label="Copiloto de IA"
          title="Copiloto de IA"
          className={cn(
            "flex size-11 shrink-0 items-center justify-center rounded-md transition-colors",
            aberto ? "bg-gold-50 text-gold-700" : "text-gold-600 hover:bg-gold-50",
          )}
        >
          {c.pensando || c.resumindo ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            <Sparkles size={20} strokeWidth={1.75} />
          )}
        </button>
      )}
    >
      {(fechar) => (
        <>
          {!soResumo &&
            (
              [
                "sugerir",
                "mais_curta",
                "mais_simpatica",
                "mais_comercial",
                "alternativa",
                "fechar_venda",
              ] as const
            ).map((a) => item(a, fechar))}
          <ItemMenu
            icone={FileText}
            disabled={c.resumindo}
            onClick={() => {
              fechar();
              c.resumir();
            }}
          >
            Resumir conversa
          </ItemMenu>
        </>
      )}
    </Popover>
  );
}

/** Faixa acima do campo: sugestões, "pensando" e resumo. */
export function BarraCopiloto({
  c,
  ocultarSugestoes,
  onEditar,
  onEnviar,
}: {
  c: Copiloto;
  ocultarSugestoes: boolean;
  onEditar: (texto: string, uso: UsoSugestaoLocal) => void;
  onEnviar: (texto: string, uso: UsoSugestaoLocal) => void;
}) {
  if (!c.disponivel) return null;
  const mostrarSugestoes = !ocultarSugestoes && (c.pensando || c.atual);
  if (!mostrarSugestoes && !c.resumo) return null;

  return (
    <div className="mx-auto max-w-3xl space-y-2 px-3 pt-2">
      {c.resumo && (
        <div
          className="relative rounded-lg bg-gold-50 p-3 pr-9 text-[13px] text-neutral-800 ring-1 ring-gold-200"
          role="note"
        >
          <p className="mb-1 flex items-center gap-1.5 text-xs font-bold text-gold-800">
            <FileText size={13} strokeWidth={2} /> Resumo da conversa
          </p>
          <p className="whitespace-pre-line">{c.resumo}</p>
          <button
            onClick={c.fecharResumo}
            aria-label="Fechar resumo"
            className="absolute right-1.5 top-1.5 rounded-md p-1.5 text-neutral-500 hover:bg-gold-100"
          >
            <X size={14} strokeWidth={1.75} />
          </button>
        </div>
      )}
      {mostrarSugestoes &&
        (c.pensando ? (
          <p className="flex h-9 items-center gap-2 text-[13px] text-gold-800" aria-live="polite">
            <Loader2 size={14} className="animate-spin" />
            {c.acaoPendente && c.acaoPendente !== "sugerir"
              ? `${ROTULO_ACAO_COPILOTO[c.acaoPendente]}…`
              : "Pensando em sugestões…"}
          </p>
        ) : (
          c.atual && <Sugestoes s={c.atual} c={c} onEditar={onEditar} onEnviar={onEnviar} />
        ))}
    </div>
  );
}

function Sugestoes({
  s,
  c,
  onEditar,
  onEnviar,
}: {
  s: SugestoesIa;
  c: Copiloto;
  onEditar: (texto: string, uso: UsoSugestaoLocal) => void;
  onEnviar: (texto: string, uso: UsoSugestaoLocal) => void;
}) {
  return (
    <section aria-label="Sugestões da IA">
      <div className="mb-1.5 flex items-center gap-1.5 text-xs text-neutral-500">
        <Sparkles size={13} strokeWidth={2} className="text-gold-600" />
        <span className="font-semibold text-neutral-700">
          {s.acao === "sugerir" ? "Sugestões" : ROTULO_ACAO_COPILOTO[s.acao]}
        </span>
        {s.consultasEstoque > 0 && <span>· conferiu o estoque do ERP</span>}
        <span className="flex-1" />
        <Popover
          lado="cima"
          largura="w-56"
          gatilho={({ alternar }) => (
            <button
              onClick={alternar}
              aria-label="Avaliar sugestões"
              title="Avaliar sugestões"
              className="rounded-md p-1.5 hover:bg-neutral-100 hover:text-neutral-700"
            >
              <ThumbsDown size={14} strokeWidth={1.75} />
            </button>
          )}
        >
          {(fechar) =>
            (["informacao_incorreta", "tom_inadequado", "nao_usar"] as const).map((a) => (
              <ItemMenu
                key={a}
                onClick={() => {
                  fechar();
                  c.avaliar(s.interacaoId, a);
                  c.descartar(s.interacaoId);
                }}
              >
                {ROTULO_AVALIACAO_IA[a]}
              </ItemMenu>
            ))
          }
        </Popover>
        <button
          onClick={() => c.pedir("sugerir")}
          aria-label="Nova sugestão"
          title="Nova sugestão"
          className="rounded-md p-1.5 hover:bg-neutral-100 hover:text-neutral-700"
        >
          <RefreshCw size={14} strokeWidth={1.75} />
        </button>
        <button
          onClick={() => c.descartar(s.interacaoId)}
          aria-label="Ignorar sugestões"
          title="Ignorar"
          className="rounded-md p-1.5 hover:bg-neutral-100 hover:text-neutral-700"
        >
          <X size={14} strokeWidth={1.75} />
        </button>
      </div>
      <ul className="rolagem-fina -mx-3 flex snap-x gap-2 overflow-x-auto px-3 pb-1 md:mx-0 md:grid md:grid-cols-3 md:overflow-visible md:px-0">
        {s.sugestoes.map((g, i) => {
          const uso = {
            interacaoId: s.interacaoId,
            indice: i,
            original: g.texto,
            reescrita: ACOES_REESCRITA.includes(s.acao),
          };
          return (
            <li key={i} className="group relative w-[78%] shrink-0 snap-start md:w-auto">
              <button
                onClick={() => onEditar(g.texto, uso)}
                title="Usar e editar"
                className="flex h-full w-full items-start rounded-lg border border-gold-200 bg-gold-50/60 p-2.5 pr-10 text-left text-[13px] leading-snug text-neutral-800 transition-colors hover:border-gold-400 hover:bg-gold-50"
              >
                <span className="line-clamp-4">{g.texto}</span>
              </button>
              <button
                onClick={() => onEnviar(g.texto, uso)}
                aria-label={`Enviar como está: ${g.texto}`}
                title="Enviar como está"
                className="absolute bottom-1.5 right-1.5 flex size-8 items-center justify-center rounded-md text-gold-700 hover:bg-gold-100"
              >
                <Send size={15} strokeWidth={1.75} />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
